import { useState, useEffect, useRef } from "react";
import { Tab, Modal, Button, Form, Dropdown } from "semantic-ui-react";
import CreatableSelect from "react-select/creatable";
import styles from "./assets.module.scss";
import Transactions from "./Transactions";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAdminUsers,
  fetchAssetProcessingEmailContent,
} from "actions/assets";
import { debounce } from "lodash";
import { toast } from "react-toastify";

function Assets({ account }) {
  const dispatch = useDispatch();
  const adminUsers =
    useSelector((state) => state.assets?.adminUser?.list?.response || []);

  const [open, setOpen] = useState(false);
  const [templateType, setTemplateType] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [message, setMessage] = useState("");
  const [previewMessage, setPreviewMessage] = useState("");
  const previewRef = useRef(null);

  const [templateError, setTemplateError] = useState("");
  const [recipientError, setRecipientError] = useState("");
  const [previewError, setPreviewError] = useState("");

  useEffect(() => {
    if (open) {
      setTemplateType("");
      setRecipients([]);
      setMessage("");
      setPreviewMessage("");
      setTemplateError("");
      setRecipientError("");
      setPreviewError("");
    }
  }, [open]);

  const isKnownUser = (email) => {
    return adminUsers.some((user) => user.email === email);
  };

  useEffect(() => {
    if (previewRef.current && previewMessage) {
      const lines = previewMessage.split("\n");
      const formatted = lines.join("<br/>");
      const styled = `<div style="font-family: Montserrat, Arial, sans-serif; font-size: 14px; color: #717274 !important; line-height: 1.5;">
        ${formatted}
      </div>`;

      if (previewRef.current.innerHTML !== styled) {
        previewRef.current.innerHTML = styled;
      }
    }
  }, [previewMessage]);

  const handleChange = (selectedOptions) => {
    setRecipients(selectedOptions || []);
    setRecipientError("");
  };

  const handleCreateOption = (inputValue) => {
    if (!isKnownUser(inputValue)) {
      setRecipientError("Invalid Email");
      return;
    }

    setRecipients((prev) => [
      ...prev,
      { label: inputValue, value: inputValue },
    ]);
    setRecipientError("");
  };

  useEffect(() => {
    if (!templateType) {
      setMessage("");
      setPreviewMessage("");
      return;
    }

    setTemplateError("");
    setPreviewError("");

    let mainContent = "";

    if (templateType === "ASSET_PROCESSING_EMAIL_TRIGGER") {
      mainContent = `Just a quick note to let you know that one or more of your assets are currently being processed on our end.
No worries though — our best minds are already on it, making sure everything is handled with care.
We’ll keep you posted once the processing is complete and the asset is good to go.

Thanks so much for your patience — we really appreciate your understanding while we work through this.
If there’s anything else you need in the meantime, we’re just a message away.`;
    } else if (templateType === "ASSET_PROCESSED_EMAIL_TRIGGER") {
      mainContent = `Good news — the asset that was being processed on our end is now all set and ready to roll!
Everything’s been taken care of, and you’re good to go from here. No action needed — just pick up where you left off.

Thanks again for your patience while we got things sorted.
As always, if you have any questions or need a hand, we’re right here.`;
    }

    setMessage(mainContent);
    setPreviewMessage(`Hi there,\n\n${mainContent}`);
  }, [templateType]);

  const handleSend = async () => {
    let valid = true;

    if (!templateType) {
      setTemplateError("Please select a template type");
      valid = false;
    }

    if (!recipients || recipients.length === 0) {
      setRecipientError("Please add at least one recipient");
      valid = false;
    }

    if (!message.trim()) {
      setPreviewError("Email preview cannot be empty");
      valid = false;
    }

    if (!valid) return;

    try {
      const cleaned = message.replace(/^Hi there,?\s*/i, "");
      const formatted = cleaned.split("\n").join("<br/>");

      const styledContent = `<div style="font-family: Montserrat, Arial, sans-serif; font-size: 14px; color: #717274 !important; line-height: 1.5;">
        ${formatted}
      </div>`;

      await dispatch(
        fetchAssetProcessingEmailContent(
          recipients.map((r) => r.value),
          templateType,
          styledContent
        )
      );

      toast.success("Email sent successfully");

      setOpen(false);
      setRecipients([]);
      setTemplateType("");
      setMessage("");
      setPreviewMessage("");
      setTemplateError("");
      setRecipientError("");
      setPreviewError("");
    } catch (err) {
      toast.error("Failed to send email");
    }
  };

  const panes = [
    {
      menuItem: "Transactions",
      render: () => (
        <Tab.Pane>
          <Transactions account={account} />
        </Tab.Pane>
      ),
    },
  ];

  return (
    <div className={styles.asset}>
      <div className={styles.header}>
        <button className={styles.notifyButton} onClick={() => setOpen(true)}>
          Notify
        </button>
      </div>

      <Tab panes={panes} />

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        size="small"
        className={styles.flatModal}
      >
        <div className={styles.modalHeader}>EMAIL TEMPLATE</div>

        <Modal.Content>
          <Form className={styles.flatForm}>
            <Form.Field>
              <label>Type Of Template</label>
              <Dropdown
                placeholder="Choose"
                fluid
                selection
                options={[
                  {
                    key: "processing",
                    text: "Asset Processing Alert",
                    value: "ASSET_PROCESSING_EMAIL_TRIGGER",
                  },
                  {
                    key: "processed",
                    text: "Asset Processed Follow up Alert",
                    value: "ASSET_PROCESSED_EMAIL_TRIGGER",
                  },
                ]}
                value={templateType}
                onChange={(e, { value }) => {
                  setTemplateType(value);
                  setTemplateError("");
                  setPreviewError("");
                }}
              />
              {templateError && (
                <span style={{ color: "#9f3a38", fontSize: "12px" }}>
                  {templateError}
                </span>
              )}
            </Form.Field>

            <Form.Field>
              <label>Recipient Email</label>
              <CreatableSelect
                isMulti
                isClearable={false}
                placeholder="Email To"
                value={recipients}
                onChange={handleChange}
                onCreateOption={handleCreateOption}
                onInputChange={debounce((inputValue) => {
                  if (inputValue && account?.id) {
                    dispatch(fetchAdminUsers(inputValue, account.id));
                  }
                }, 300)}
                options={adminUsers.map((user) => ({
                  label: user.email,
                  value: user.email,
                }))}
                components={{
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null,
                }}
                formatCreateLabel={() => null}
                styles={{
                  multiValue: (base) => ({
                    ...base,
                    borderRadius: "20px",
                    backgroundColor: "#e0e0e0",
                    padding: "2px 8px",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#333",
                    fontWeight: 500,
                    fontSize: "13px",
                    padding: "0 4px",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#666",
                    ":hover": {
                      backgroundColor: "transparent",
                      color: "#000",
                    },
                  }),
                }}
              />
              {recipientError && (
                <span style={{ color: "#9f3a38", fontSize: "12px" }}>
                  {recipientError}
                </span>
              )}
            </Form.Field>


            <Form.Field>
              <label>Email Preview</label>
              <div
                ref={previewRef}
                className={styles.emailPreview}
                contentEditable
                suppressContentEditableWarning
                onInput={(e) => {
                  setMessage(e.currentTarget.innerText);
                  setPreviewError("");
                }}
              />
              {previewError && (
                <span style={{ color: "#9f3a38", fontSize: "12px" }}>
                  {previewError}
                </span>
              )}
            </Form.Field>
          </Form>

          <div className={styles.modalActions}>
            <Button className={styles.sendButton} onClick={handleSend}>
              Send
            </Button>
            <Button
              className={styles.cancelButton}
              onClick={() => {
                setOpen(false);
                setTemplateType("");
                setRecipients([]);
                setMessage("");
                setPreviewMessage("");
                setTemplateError("");
                setRecipientError("");
                setPreviewError("");
              }}
            >
              Cancel
            </Button>
          </div>
        </Modal.Content>
      </Modal>
    </div>
  );
}

export default Assets;
